import * as React from 'react';
import { useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import FiltersHub from '../../forms/filters-hub';
import SearchBar from '../../layout/custom-screen/search-bar';
import Iconify from '../../media/iconify';
import CustomPopover, { usePopover } from '../../overlays/custom-popover';

interface TableActionsProps {
  filters?: any;
  tableFilters?: React.ReactNode;
  options?: React.ReactNode | ((popover: any) => React.ReactNode);
  handleFilterSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilters?: (key: string, value: any) => void;
  // Enhanced props for FiltersHub support
  filterFields?: any[];
  onResetFilters?: () => void;
  canReset?: boolean;
}

export default function TableActions({
  filters,
  tableFilters,
  options,
  handleFilterSearch,
  handleFilters,
  filterFields,
  onResetFilters,
  canReset,
}: TableActionsProps) {
  const popover = usePopover();

  // Adapter function to convert handleFilters to the format expected by FiltersHub
  const handleFiltersChange = useCallback(
    (newFilters: any) => {
      if (handleFilters) {
        // If newFilters is a function, call it with current filters
        if (typeof newFilters === 'function') {
          const updatedFilters = newFilters(filters);
          // Apply each changed field
          Object.keys(updatedFilters).forEach((key) => {
            if (updatedFilters[key] !== filters[key]) {
              handleFilters(key, updatedFilters[key]);
            }
          });
        } else {
          // Direct object update
          Object.keys(newFilters).forEach((key) => {
            if (newFilters[key] !== filters[key]) {
              handleFilters(key, newFilters[key]);
            }
          });
        }
      }
    },
    [handleFilters, filters]
  );

  // Render options, passing popover if options is a function
  const renderOptions = useMemo(() => {
    if (typeof options === 'function') {
      return options(popover);
    }
    return options;
  }, [options, popover]);

  const stackStyles = useMemo(
    () => ({
      p: { xs: 1.5, sm: 2, md: 2 },
      pr: { xs: 1.5, sm: 1, md: 1 },
      pb: { xs: 1.5, sm: 1.5, md: 1.5 },
      gap: { xs: 1, sm: 1.5, md: 2 },
    }),
    []
  );

  return (
    <>
      <Stack
        spacing={{ xs: 1.5, sm: 2 }} // Responsive spacing
        alignItems="center"
        direction="row"
        sx={stackStyles}
      >
        <SearchBar
          filters={filters}
          handleFilterSearch={handleFilterSearch}
          handleFilters={handleFilters}
        />

        {tableFilters}

        {filterFields && filterFields.length > 0 && (
          <FiltersHub
            filters={filters}
            onChange={handleFiltersChange}
            fields={filterFields}
            onResetFilters={onResetFilters}
            canReset={canReset}
          />
        )}

        <Box sx={{ flexGrow: 1 }} />

        {options && (
          <>
            <IconButton color="inherit" onClick={popover.onOpen}>
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>

            <CustomPopover
              open={popover.open}
              onClose={popover.onClose}
              arrow="right-top"
              sx={{ width: 140 }}
            >
              {renderOptions}
            </CustomPopover>
          </>
        )}
      </Stack>
    </>
  );
}
